import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set, push, update } from 'firebase/database';
import { fetchAdminWorkspace } from '../../services/lmsDataCache';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBranches } from '../../hooks/useBranches';
import Button from '../ui/Button';
import {
  WorkspacePageError,
  WorkspacePageLoader,
} from '../ui/Workspace';
import PaymentSheet from '../reception/kiosk/PaymentSheet';
import CredentialsCard from '../reception/kiosk/CredentialsCard';
import {
  assignStudentToBatch,
  buildQuickRegisterCredentials,
  recordAdhocReceptionPayment,
  recordReceptionPayment,
  removeEnrollmentRecord,
} from '../../utils/lmsMutations';
import {
  buildAcademyStudentId,
  resolveActiveCourseContexts,
} from '../../utils/studentIdentity';
import { resolveBranchRef } from '../../utils/branchResolver';

const normalizePhone = (value) => {
  const cleaned = String(value || '').trim().replace(/\s+/g, '');
  const local = cleaned.startsWith('+94')
    ? `0${cleaned.slice(3)}`
    : cleaned.startsWith('94')
      ? `0${cleaned.slice(2)}`
      : cleaned;
  const digits = local.replace(/\D/g, '');
  if (!digits) return '';
  return digits.startsWith('0') ? digits : `0${digits}`;
};

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const getStudentEmail = (student) =>
  normalizeEmail(student?.personalDetails?.email || student?.email);

const userRecordHasLogin = (user) =>
  Boolean(user?.authCreated || user?.authCreatedAt || user?.authLinkedUid);

const hasStudentLogin = (student) =>
  Boolean(student?.authCreated || student?.authLinkedUid || student?.loginUserUid);

const canManageStudentLogin = (student) => {
  const authUid = student?.authLinkedUid || student?.loginUserUid || student?.id;
  return hasStudentLogin(student) && String(authUid || '') === String(student?.id || '');
};

const shouldShowStudentRecord = (student, userById = {}, usersByEmail = new Map()) => {
  const email = getStudentEmail(student);
  const linkedUser = userById[student.id] || {};
  const emailUser = email ? usersByEmail.get(email) : null;
  const roles = [student.role, linkedUser.role, emailUser?.role]
    .map((role) => String(role || '').toLowerCase())
    .filter(Boolean);

  if (roles.some((role) => role !== 'student')) return false;
  return true;
};

const splitName = (value) => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] || '', lastName: '' };
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts.slice(-1).join(''),
  };
};

const parseBulkRows = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.includes('\t')
        ? line.split('\t')
        : line.split(',').map((part) => part.trim());
      const [name = '', email = '', phone = ''] = parts;
      const { firstName, lastName } = splitName(name);
      return {
        firstName,
        lastName,
        email: email.trim().toLowerCase(),
        phone: normalizePhone(phone),
      };
    })
    .filter((row) => row.firstName && row.email);

const getDisplayName = (student) =>
  `${student.personalDetails?.firstName || student.firstName || ''} ${
    student.personalDetails?.lastName || student.lastName || ''
  }`.trim() || student.email || 'Student';

const getPlatformAssignedPassword = (student, activeCourses = []) => {
  const { prefix, studentNumber } = getPlatformPasswordParts(student, activeCourses);
  if (!prefix || !studentNumber) return '';
  return `${prefix}${studentNumber}`;
};

const getPlatformPasswordParts = (student, activeCourses = []) => {
  const prefix =
    student?.batchNumber ||
    student?.courseCode ||
    activeCourses[0]?.batchName ||
    activeCourses[0]?.courseCode ||
    activeCourses[0]?.courseId ||
    '';
  const studentNumber =
    student?.studentNumber ||
    student?.platformStudentNumber ||
    student?.academyStudentId ||
    '';

  return { prefix, studentNumber };
};

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const toPaymentStudent = (student) => ({
  ...student,
  displayName: getDisplayName(student),
  academyStudentId: student.academyStudentId || '',
});

const getStudentBranchRef = (student, activeCourses = [], branchCatalogs = {}) => {
  const directBranch = resolveBranchRef(
    {
      branchId: student.branchId || student.personalDetails?.branchId,
      branch: student.branch || student.personalDetails?.branch || student.publicProfile?.branch,
      branchName: student.branchName || student.personalDetails?.branchName,
    },
    branchCatalogs,
  );
  if (directBranch?.name) return directBranch;

  for (const course of activeCourses) {
    const resolved = resolveBranchRef(course.batch || course.branch, branchCatalogs);
    if (resolved?.name) return resolved;
  }

  return null;
};

const getStudentBranchLabel = (student, activeCourses = [], branchCatalogs = {}) =>
  getStudentBranchRef(student, activeCourses, branchCatalogs)?.name || 'Branch not set';

const StudentsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    activeList: activeBranches,
    byId: branchesById,
    bySlug: branchesBySlug,
  } = useBranches();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState({});
  const [enrollments, setEnrollments] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [listGeneratorOpen, setListGeneratorOpen] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [paymentStudent, setPaymentStudent] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [detailStudentId, setDetailStudentId] = useState('');
  const [credentialsResult, setCredentialsResult] = useState(null);
  const [courseFilter, setCourseFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkBranchValue, setBulkBranchValue] = useState('');
  const [bulkCourseBatchValue, setBulkCourseBatchValue] = useState('');
  const [savingBulkAction, setSavingBulkAction] = useState(false);

  const loadStudents = async ({ force = false } = {}) => {
    setLoading(true);
    try {
      const workspace = await fetchAdminWorkspace({ force });
      const studentsData = workspace.students || {};
      const coursesData = workspace.courses || {};
      const enrollmentsData = Object.fromEntries(
        (workspace.enrollments || []).map((item) => [item.id, item]),
      );
      const requestsData = Object.fromEntries(
        (workspace.paymentRequests || []).map((item) => [item.id, item]),
      );
      const paymentsData = Object.fromEntries(
        (workspace.payments || []).map((item) => [item.id, item]),
      );
      const usersData = workspace.users || {};
      const usersByEmail = new Map();

      Object.entries(usersData).forEach(([userId, user]) => {
        const email = normalizeEmail(user?.email || user?.personalDetails?.email);
        if (!email) return;
        const existing = usersByEmail.get(email);
        if (!existing || userRecordHasLogin(user)) {
          usersByEmail.set(email, { id: userId, ...(user || {}) });
        }
      });

      setCourses(coursesData);
      setEnrollments(
        Object.entries(enrollmentsData).map(([id, item]) => ({ id, ...(item || {}) })),
      );
      setPaymentRequests(
        Object.entries(requestsData).map(([id, item]) => ({ id, ...(item || {}) })),
      );
      setPayments(
        Object.entries(paymentsData).map(([id, item]) => ({ id, ...(item || {}) })),
      );
      setStudents(
        Object.entries(studentsData)
          .map(([id, student]) => {
            const studentData = { id, ...(student || {}) };
            const email = getStudentEmail(studentData);
            const userById = usersData[id] || {};
            const emailUser = email ? usersByEmail.get(email) : null;
            const sameIdHasLogin = userRecordHasLogin(userById);
            const emailHasLogin = userRecordHasLogin(emailUser);
            const loginUserUid = sameIdHasLogin
              ? id
              : emailHasLogin
                ? emailUser.id
                : '';

            return {
              ...studentData,
              authCreated: Boolean(studentData.authCreated || sameIdHasLogin || emailHasLogin),
              authLinkedUid: studentData.authLinkedUid || loginUserUid || '',
              loginUserUid,
              loginUserRole: loginUserUid
                ? String((loginUserUid === id ? userById : emailUser)?.role || '').toLowerCase()
                : '',
            };
          })
          .filter((student) => shouldShowStudentRecord(student, usersData, usersByEmail)),
      );
      setError(null);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const coursesList = useMemo(
    () => Object.entries(courses).map(([id, course]) => ({ id, ...(course || {}) })),
    [courses],
  );
  const branchCatalogs = useMemo(
    () => ({ byId: branchesById, bySlug: branchesBySlug }),
    [branchesById, branchesBySlug],
  );

  const enrollmentsByStudent = useMemo(() => {
    const grouped = {};
    enrollments.forEach((enrollment) => {
      grouped[enrollment.studentId] = grouped[enrollment.studentId] || [];
      grouped[enrollment.studentId].push(enrollment);
    });
    return grouped;
  }, [enrollments]);

  const studentRows = useMemo(
    () =>
      students.map((student) => ({
        student,
        activeCourses: resolveActiveCourseContexts({
          enrollments: enrollmentsByStudent[student.id] || [],
          courses,
        }),
      })),
    [students, enrollmentsByStudent, courses],
  );

  /** Full storage rows — used for bulk assign/remove so branch + batchIndex resolve correctly. */
  const batchAssignOptions = useMemo(() => {
    const options = [];
    coursesList.forEach((course) => {
      if (courseFilter && course.id !== courseFilter) return;
      (course.batches || []).forEach((batch, index) => {
        const batchId = batch.batchId || String(index);
        const branchLabel =
          resolveBranchRef(batch, branchCatalogs)?.name ||
          batch.branchName ||
          batch.branch ||
          'Branch not set';
        options.push({
          value: `${course.id}::${batchId}::${index}`,
          label: `${batchId} - ${branchLabel} - ${course.name || course.title || course.id}`,
          courseId: course.id,
          batchId,
          batchIndex: index,
          branchId: batch.branchId || '',
          branchName: batch.branchName || branchLabel,
        });
      });
    });
    return options;
  }, [coursesList, courseFilter, branchCatalogs]);

  /** One entry per course + batchId (multi-branch shares one logical batch). */
  const batchFilterOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    coursesList.forEach((course) => {
      if (courseFilter && course.id !== courseFilter) return;
      (course.batches || []).forEach((batch, index) => {
        const batchId = batch.batchId || String(index);
        const dedupeKey = `${course.id}::${batchId}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        options.push({
          value: `${course.id}::${batchId}`,
          label: `${batchId} — ${course.name || course.title || course.id}`,
        });
      });
    });
    return options;
  }, [coursesList, courseFilter]);

  const branchOptions = useMemo(
    () => activeBranches.filter((branch) => branch?.id && branch?.name),
    [activeBranches],
  );

  const filteredRows = useMemo(() => {
    const batchParts = batchFilter.split('::');
    const batchCourseId = batchParts[0] || '';
    const batchId = batchParts[1] || '';
    const batchIndexPart = batchParts[2];
    return studentRows.filter(({ student, activeCourses }) => {
      if (courseFilter && !activeCourses.some((course) => course.courseId === courseFilter)) {
        return false;
      }
      if (
        batchFilter &&
        !activeCourses.some(
          (course) =>
            String(course.courseId) === String(batchCourseId) &&
            String(course.batchId) === String(batchId) &&
            (batchIndexPart === undefined ||
              batchIndexPart === '' ||
              course.batchIndex == null ||
              String(course.batchIndex) === String(batchIndexPart)),
        )
      ) {
        return false;
      }
      if (branchFilter) {
        const resolvedBranch = getStudentBranchRef(student, activeCourses, branchCatalogs);
        const matches =
          resolvedBranch?.id === branchFilter ||
          resolvedBranch?.slug === branchFilter ||
          resolvedBranch?.name === branchFilter;
        if (!matches) return false;
      }
      return true;
    });
  }, [studentRows, courseFilter, batchFilter, branchFilter, branchCatalogs]);

  useEffect(() => {
    setPage(1);
  }, [courseFilter, batchFilter, branchFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const pagedStudentIds = pagedRows.map(({ student }) => student.id);
  const allPagedSelected =
    pagedStudentIds.length > 0 &&
    pagedStudentIds.every((studentId) => selectedStudentIds.includes(studentId));

  const detailStudent = useMemo(
    () => students.find((student) => student.id === detailStudentId) || null,
    [students, detailStudentId],
  );

  const detailActiveCourses = detailStudent
    ? resolveActiveCourseContexts({
        enrollments: enrollmentsByStudent[detailStudent.id] || [],
        courses,
      })
    : [];

  const detailOpenRequests = detailStudent
    ? paymentRequests.filter(
        (request) =>
          request.studentId === detailStudent.id &&
          ['pending', 'processing'].includes(request.status),
      )
    : [];
  const detailPaymentSchedule = detailStudent
    ? [
        ...payments
          .filter((payment) => payment.studentId === detailStudent.id)
          .map((payment) => ({
            id: `payment-${payment.id}`,
            kind: 'paid',
            courseName: payment.courseName || 'Course',
            label: payment.paymentCycleLabel || payment.paymentType || 'Payment',
            amount: Number(payment.amount || 0),
            date: payment.paymentDate || payment.createdAt || '',
            status: payment.status || 'completed',
            method: payment.paymentMethod || '',
          })),
        ...paymentRequests
          .filter(
            (request) =>
              request.studentId === detailStudent.id &&
              ['pending', 'processing'].includes(request.status),
          )
          .map((request) => ({
            id: `request-${request.id}`,
            kind: 'upcoming',
            courseName: request.courseName || 'Course',
            label: request.paymentCycleLabel || request.paymentType || 'Payment',
            amount: Number(request.amount || 0),
            date: request.dueDate || request.createdAt || '',
            status: request.status || 'pending',
            method: '',
          })),
      ].sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'upcoming' ? -1 : 1;
        return new Date(a.date || 0) - new Date(b.date || 0);
      })
    : [];

  const createCredentialsForStudent = async (student) => {
    const email = student.personalDetails?.email || student.email;
    if (!email) {
      throw new Error('Student email is required before creating an account.');
    }

    let batchNumber = student.batchNumber;
    let studentNumber = student.studentNumber;
    if (!batchNumber || !studentNumber) {
      const firstEnrollment = enrollmentsByStudent[student.id]?.[0];
      if (!firstEnrollment) {
        throw new Error('Assign the student to a course before creating an account.');
      }
      const credentials = await buildQuickRegisterCredentials({
        courseId: firstEnrollment.courseId,
        batchId: firstEnrollment.batchId || '0',
      });
      batchNumber = credentials.batchNumber;
      studentNumber = credentials.studentNumber;
    }

    const password = `${batchNumber}${studentNumber}`;
    const createStudentAuthAccount = httpsCallable(functions, 'createStudentAuthAccount');
    const result = await createStudentAuthAccount({
      uid: student.id,
      email,
      password,
      firstName: student.personalDetails?.firstName || student.firstName || '',
      lastName: student.personalDetails?.lastName || student.lastName || '',
      batchNumber,
      studentNumber,
    });

    return {
      uid: student.id,
      studentName: getDisplayName(student),
      academyStudentId: student.academyStudentId || '',
      email,
      phone: student.personalDetails?.phone || student.phone || '',
      password: result.data?.password || password,
      loginUrl: `${window.location.origin}/login`,
    };
  };

  const handleBulkAdd = async ({ text, courseId, batchId, branch }) => {
    const rows = parseBulkRows(text);
    if (!rows.length) throw new Error('Paste at least one valid student row.');
    if (!courseId) throw new Error('Select a course.');

    setSavingBulk(true);
    try {
      for (const row of rows) {
        const studentRef = push(ref(db, 'students'));
        const uid = studentRef.key;
        const timestamp = new Date().toISOString();
        const academyStudentId = buildAcademyStudentId(uid, timestamp);
        const { batchNumber, studentNumber } = await buildQuickRegisterCredentials({
          courseId,
          batchId: batchId || '0',
        });
        const payload = {
          uid,
          userId: uid,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
          phone: row.phone,
          role: 'student',
          status: 'active',
          authCreated: false,
          academyStudentId,
          branchId: branch?.id || '',
          branchName: branch?.name || '',
          branch: branch?.slug || branch?.name || '',
          batchNumber,
          studentNumber,
          createdAt: timestamp,
          updatedAt: timestamp,
          registrationDate: timestamp,
          registrationSource: 'admin_bulk_import',
          personalDetails: {
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            phone: row.phone,
            branchId: branch?.id || '',
            branchName: branch?.name || '',
            branch: branch?.slug || branch?.name || '',
          },
        };

        await Promise.all([
          set(studentRef, payload),
          set(ref(db, `users/${uid}`), payload),
        ]);
        await assignStudentToBatch({ studentId: uid, courseId, batchId: batchId || '0' });
      }
      setBulkOpen(false);
      await loadStudents({ force: true });
    } finally {
      setSavingBulk(false);
    }
  };

  const handleCreateAccount = async (student) => {
    try {
      const credentials = await createCredentialsForStudent(student);
      setCredentialsResult(credentials);
      await loadStudents({ force: true });
    } catch (err) {
      console.error('Error creating student auth account:', err);
      alert(err.message || 'Could not create account.');
    }
  };

  const handleDeleteAccount = async (student) => {
    if (!window.confirm(`Delete login account for ${getDisplayName(student)}? Student records will remain.`)) {
      return;
    }

    try {
      const deleteStudentAuthAccount = httpsCallable(functions, 'deleteStudentAuthAccount');
      await deleteStudentAuthAccount({ uid: student.id });
      await loadStudents({ force: true });
    } catch (err) {
      console.error('Error deleting student auth account:', err);
      alert(err.message || 'Could not delete account.');
    }
  };

  const handleDelete = async (studentId) => {
    try {
      const deleteManagedUser = httpsCallable(functions, 'deleteManagedUser');
      await deleteManagedUser({ uid: studentId });
      setStudents(students.filter((student) => student.id !== studentId));
      setSelectedStudentIds((ids) => ids.filter((id) => id !== studentId));
      if (detailStudentId === studentId) setDetailStudentId('');
      alert('Student deleted successfully!');
    } catch (err) {
      console.error('Error deleting student:', err);
      alert(err.message || 'Failed to delete student. Please try again.');
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds((ids) =>
      ids.includes(studentId)
        ? ids.filter((id) => id !== studentId)
        : [...ids, studentId],
    );
  };

  const togglePageSelection = () => {
    setSelectedStudentIds((ids) => {
      if (allPagedSelected) {
        return ids.filter((id) => !pagedStudentIds.includes(id));
      }
      return Array.from(new Set([...ids, ...pagedStudentIds]));
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedStudentIds.length) return;
    if (
      !window.confirm(
        `Delete ${selectedStudentIds.length} selected student${
          selectedStudentIds.length === 1 ? '' : 's'
        }? This will also remove their login accounts.`,
      )
    ) {
      return;
    }

    setSavingBulkAction(true);
    try {
      const deleteManagedUser = httpsCallable(functions, 'deleteManagedUser');
      await Promise.all(selectedStudentIds.map((uid) => deleteManagedUser({ uid })));
      setStudents((items) =>
        items.filter((student) => !selectedStudentIds.includes(student.id)),
      );
      if (selectedStudentIds.includes(detailStudentId)) setDetailStudentId('');
      setSelectedStudentIds([]);
      alert('Selected students deleted successfully!');
    } catch (err) {
      console.error('Error deleting selected students:', err);
      alert(err.message || 'Failed to delete selected students. Please try again.');
    } finally {
      setSavingBulkAction(false);
    }
  };

  const handleBulkBranchUpdate = async () => {
    if (!selectedStudentIds.length) return;
    const selectedBranch = branchesById[bulkBranchValue];
    if (!selectedBranch) {
      alert('Select or enter a branch.');
      return;
    }

    setSavingBulkAction(true);
    try {
      const timestamp = new Date().toISOString();
      const updates = {};
      selectedStudentIds.forEach((uid) => {
        updates[`students/${uid}/branchId`] = selectedBranch.id;
        updates[`students/${uid}/branchName`] = selectedBranch.name || '';
        updates[`students/${uid}/branch`] = selectedBranch.slug || selectedBranch.name || '';
        updates[`students/${uid}/personalDetails/branchId`] = selectedBranch.id;
        updates[`students/${uid}/personalDetails/branchName`] = selectedBranch.name || '';
        updates[`students/${uid}/personalDetails/branch`] =
          selectedBranch.slug || selectedBranch.name || '';
        updates[`students/${uid}/updatedAt`] = timestamp;
        updates[`users/${uid}/branchId`] = selectedBranch.id;
        updates[`users/${uid}/branchName`] = selectedBranch.name || '';
        updates[`users/${uid}/branch`] = selectedBranch.slug || selectedBranch.name || '';
        updates[`users/${uid}/personalDetails/branchId`] = selectedBranch.id;
        updates[`users/${uid}/personalDetails/branchName`] = selectedBranch.name || '';
        updates[`users/${uid}/personalDetails/branch`] =
          selectedBranch.slug || selectedBranch.name || '';
        updates[`users/${uid}/updatedAt`] = timestamp;
      });
      await update(ref(db), updates);
      setStudents((items) =>
        items.map((student) =>
          selectedStudentIds.includes(student.id)
            ? {
                ...student,
                branchId: selectedBranch.id,
                branchName: selectedBranch.name || '',
                branch: selectedBranch.slug || selectedBranch.name || '',
                updatedAt: timestamp,
                personalDetails: {
                  ...(student.personalDetails || {}),
                  branchId: selectedBranch.id,
                  branchName: selectedBranch.name || '',
                  branch: selectedBranch.slug || selectedBranch.name || '',
                },
              }
            : student,
        ),
      );
      setBulkBranchValue('');
      alert('Branch updated for selected students.');
    } catch (err) {
      console.error('Error updating selected student branches:', err);
      alert(err.message || 'Failed to update branch. Please try again.');
    } finally {
      setSavingBulkAction(false);
    }
  };

  const handleBulkCourseBatchAssign = async () => {
    if (!selectedStudentIds.length) return;
    const selectedBatch = batchAssignOptions.find(
      (option) => option.value === bulkCourseBatchValue,
    );
    if (!selectedBatch) {
      alert('Select a course batch.');
      return;
    }

    setSavingBulkAction(true);
    try {
      await Promise.all(
        selectedStudentIds.map((studentId) =>
          assignStudentToBatch({
            studentId,
            courseId: selectedBatch.courseId,
            batchId: selectedBatch.batchId,
            batchIndex: selectedBatch.batchIndex,
            branchId: selectedBatch.branchId,
          }),
        ),
      );
      setBulkCourseBatchValue('');
      setSelectedStudentIds([]);
      await loadStudents({ force: true });
      alert('Selected students assigned to the course batch.');
    } catch (err) {
      console.error('Error assigning selected students:', err);
      alert(err.message || 'Failed to assign selected students. Please try again.');
    } finally {
      setSavingBulkAction(false);
    }
  };

  const handleBulkCourseBatchRemoval = async () => {
    if (!selectedStudentIds.length) return;
    const selectedBatch = batchAssignOptions.find(
      (option) => option.value === bulkCourseBatchValue,
    );
    if (!selectedBatch) {
      alert('Select the assigned course batch to remove.');
      return;
    }
    if (
      !window.confirm(
        `Remove the selected students from ${selectedBatch.label}? Their student records will stay in the system.`,
      )
    ) {
      return;
    }

    const matchingEnrollments = enrollments.filter(
      (enrollment) =>
        selectedStudentIds.includes(enrollment.studentId) &&
        enrollment.courseId === selectedBatch.courseId &&
        String(enrollment.batchId) === String(selectedBatch.batchId) &&
        (selectedBatch.batchIndex == null ||
          enrollment.batchIndex == null ||
          String(enrollment.batchIndex) === String(selectedBatch.batchIndex)) &&
        enrollment.status === 'active',
    );

    if (!matchingEnrollments.length) {
      alert('None of the selected students are active in that course batch.');
      return;
    }

    setSavingBulkAction(true);
    try {
      await Promise.all(
        matchingEnrollments.map((enrollment) =>
          removeEnrollmentRecord({
            enrollmentId: enrollment.id,
            studentId: enrollment.studentId,
            courseId: enrollment.courseId,
            batchId: enrollment.batchId,
            batchIndex: enrollment.batchIndex,
            branchId: enrollment.branchId,
          }),
        ),
      );
      setBulkCourseBatchValue('');
      setSelectedStudentIds([]);
      await loadStudents({ force: true });
      alert(`Removed ${matchingEnrollments.length} assigned batch record(s).`);
    } catch (err) {
      console.error('Error removing selected student batch assignments:', err);
      alert(err.message || 'Failed to remove selected batch assignments.');
    } finally {
      setSavingBulkAction(false);
    }
  };

  const handlePaymentSubmit = async (payload) => {
    if (!paymentStudent) return;
    setSavingPayment(true);
    setPaymentError('');
    try {
      if (payload.mode === 'request') {
        await recordReceptionPayment({
          paymentRequest: payload.paymentRequest,
          paymentMethod: payload.paymentMethod,
          receiptNumber: payload.receiptNumber,
          notes: payload.notes,
          paymentType: payload.paymentType,
          paymentCycle: payload.paymentCycle,
          paymentCycleLabel: payload.paymentCycleLabel,
          operator: currentUser,
        });
      } else {
        await recordAdhocReceptionPayment({
          studentId: paymentStudent.id,
          studentName: getDisplayName(paymentStudent),
          courseId: payload.course.courseId,
          courseName: payload.course.courseName,
          batchId: payload.course.batchId,
          batchName: payload.course.batchName,
          amount: payload.amount,
          paymentMethod: payload.paymentMethod,
          receiptNumber: payload.receiptNumber,
          notes: payload.notes,
          paymentType: payload.paymentType,
          paymentCycle: payload.paymentCycle,
          paymentCycleLabel: payload.paymentCycleLabel,
          operator: currentUser,
        });
      }
      setPaymentStudent(null);
      await loadStudents({ force: true });
    } catch (err) {
      console.error('Failed to record payment:', err);
      setPaymentError(err.message || 'Could not record payment.');
    } finally {
      setSavingPayment(false);
    }
  };

  if (loading) return <WorkspacePageLoader />;
  if (error) return <WorkspacePageError>{error}</WorkspacePageError>;

  const paymentActiveCourses = paymentStudent
    ? resolveActiveCourseContexts({
        enrollments: enrollmentsByStudent[paymentStudent.id] || [],
        courses,
      })
    : [];
  const openRequestsForPaymentStudent = paymentStudent
    ? paymentRequests.filter(
        (request) =>
          request.studentId === paymentStudent.id &&
          ['pending', 'processing'].includes(request.status),
      )
    : [];
  const paymentHistoryForPaymentStudent = paymentStudent
    ? payments.filter((payment) => payment.studentId === paymentStudent.id)
    : [];

  return (
    <div className="mx-auto w-full max-w-[min(100%,96rem)] space-y-3 py-1">
      {/* Page header */}
      <header className="rounded-lg border border-stone-200 bg-white px-4 py-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">Workspace</p>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-stone-950">Students</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => setListGeneratorOpen(true)}>Generate list</Button>
            <Button type="button" variant="secondary" onClick={() => setBulkOpen(true)}>Bulk add</Button>
            <Button type="button" onClick={() => navigate('/add-student')}>Add Student</Button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
          <SPageStat label="Total Students" value={students.length} />
          <SPageStat label="Showing" value={filteredRows.length} />
          <SPageStat label="Page" value={`${currentPage} / ${totalPages}`} />
          <SPageStat label="Selected" value={selectedStudentIds.length} />
        </div>
      </header>

      {/* Filters */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-3 py-2">
          <h2 className="text-base font-semibold tracking-tight text-stone-950">Filters</h2>
        </div>
        <div className="grid gap-2 px-3 py-3 md:grid-cols-4">
          <SPageField label="Course">
            <select value={courseFilter} onChange={(e) => { setCourseFilter(e.target.value); setBatchFilter(''); }} className="ledger-input min-h-[38px]">
              <option value="">All courses</option>
              {coursesList.map((c) => <option key={c.id} value={c.id}>{c.name || c.title || c.id}</option>)}
            </select>
          </SPageField>
          <SPageField label="Batch">
            <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)} className="ledger-input min-h-[38px]">
              <option value="">All batches</option>
              {batchFilterOptions.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </SPageField>
          <SPageField label="Branch">
            <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className="ledger-input min-h-[38px]">
              <option value="">All branches</option>
              {branchOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </SPageField>
          <SPageField label="Page size">
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="ledger-input min-h-[38px]">
              {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s} rows</option>)}
            </select>
          </SPageField>
        </div>
        <div className="flex items-center justify-between border-t border-stone-100 px-3 py-2 text-xs text-stone-500">
          <span>
            Showing {filteredRows.length ? (currentPage - 1) * pageSize + 1 : 0}–{Math.min(currentPage * pageSize, filteredRows.length)} of {filteredRows.length} students
          </span>
          <button type="button" onClick={() => { setCourseFilter(''); setBatchFilter(''); setBranchFilter(''); }} className="font-semibold text-stone-700 hover:text-stone-950">
            Clear filters
          </button>
        </div>
      </section>

      {/* Bulk actions bar — only when students are selected */}
      {selectedStudentIds.length ? (
        <section className="rounded-lg border border-stone-200 bg-white px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-stone-800">{selectedStudentIds.length} selected</span>
            <select value={bulkBranchValue} onChange={(e) => setBulkBranchValue(e.target.value)} className="ledger-input min-h-[36px] min-w-[160px] text-xs">
              <option value="">Select branch</option>
              {branchOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button type="button" onClick={handleBulkBranchUpdate} disabled={savingBulkAction} className="rounded border border-stone-200 bg-stone-950 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Update branch</button>
            <select value={bulkCourseBatchValue} onChange={(e) => setBulkCourseBatchValue(e.target.value)} className="ledger-input min-h-[36px] min-w-[220px] text-xs">
              <option value="">Select course batch</option>
              {batchAssignOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button type="button" onClick={handleBulkCourseBatchAssign} disabled={savingBulkAction || !bulkCourseBatchValue} className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 disabled:opacity-50">Assign batch</button>
            <button type="button" onClick={handleBulkCourseBatchRemoval} disabled={savingBulkAction || !bulkCourseBatchValue} className="rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50">Remove batch</button>
            <button type="button" onClick={handleBulkDelete} disabled={savingBulkAction} className="rounded border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">Delete selected</button>
            <button type="button" onClick={() => setSelectedStudentIds([])} disabled={savingBulkAction} className="rounded border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 disabled:opacity-50">Clear</button>
          </div>
        </section>
      ) : null}

      {/* Students table */}
      <section className="rounded-lg border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-3 py-2">
          <h2 className="text-base font-semibold tracking-tight text-stone-950">Student roster</h2>
          <p className="text-xs text-stone-500">Click any row to view full student details.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="w-10 border-b border-stone-200 bg-stone-50 px-3 py-2">
                  <input type="checkbox" checked={allPagedSelected} onChange={togglePageSelection} aria-label="Select students on this page" className="h-4 w-4 rounded border-stone-300" />
                </th>
                {['Name', 'Email', 'Phone', 'Branch', 'Courses', 'Account', ''].map((col) => (
                  <th key={col} className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map(({ student, activeCourses }) => {
                const branchLabel = getStudentBranchLabel(student, activeCourses, branchCatalogs);
                return (
                  <tr key={student.id} onClick={() => setDetailStudentId(student.id)} className="group cursor-pointer">
                    <td className="border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedStudentIds.includes(student.id)} onChange={() => toggleStudentSelection(student.id)} aria-label={`Select ${getDisplayName(student)}`} className="h-4 w-4 rounded border-stone-300" />
                    </td>
                    <td className="border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50">
                      <p className="font-semibold text-stone-950">{getDisplayName(student)}</p>
                      <p className="text-xs text-stone-500">{student.academyStudentId || student.studentNumber || ''}</p>
                    </td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">{student.personalDetails?.email || student.email}</td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">{student.personalDetails?.phone || student.phone}</td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">{branchLabel}</td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">{activeCourses.length} active</td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${hasStudentLogin(student) ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-amber-200'}`}>
                        {hasStudentLogin(student) ? 'Login' : 'No login'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-right group-hover:bg-stone-50">
                      <div className="flex justify-end gap-3">
                        {!hasStudentLogin(student) ? (
                          <button type="button" onClick={(e) => { e.stopPropagation(); handleCreateAccount(student); }} className="text-xs font-semibold text-stone-600 hover:text-stone-900">Create account</button>
                        ) : null}
                        <button type="button" onClick={(e) => { e.stopPropagation(); setPaymentStudent(student); }} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900">Payment</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); navigate('/add-student', { state: { existingStudent: student } }); }} className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]">Edit</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(student.id); }} className="text-xs font-semibold text-red-600 hover:text-red-900">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!pagedRows.length ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-stone-500">No students match the selected filters.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 px-3 py-2">
          <p className="text-xs text-stone-500">
            Page {currentPage} of {totalPages} · {filteredRows.length} students
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPage((v) => Math.max(1, v - 1))} disabled={currentPage <= 1} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 disabled:opacity-40">Previous</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
              const p = start + i;
              return (
                <button key={p} type="button" onClick={() => setPage(p)} className={`rounded border px-2.5 py-1 text-xs font-semibold ${p === currentPage ? 'border-stone-950 bg-stone-950 text-white' : 'border-stone-200 text-stone-700 hover:bg-stone-50'}`}>{p}</button>
              );
            })}
            <button type="button" onClick={() => setPage((v) => Math.min(totalPages, v + 1))} disabled={currentPage >= totalPages} className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 disabled:opacity-40">Next</button>
          </div>
        </div>
      </section>

      <BulkImportModal
        open={bulkOpen}
        courses={coursesList}
        branchOptions={branchOptions}
        branchCatalogs={branchCatalogs}
        saving={savingBulk}
        onClose={() => setBulkOpen(false)}
        onSubmit={handleBulkAdd}
      />

      <StudentListGeneratorModal
        open={listGeneratorOpen}
        rows={studentRows}
        courses={coursesList}
        branchOptions={branchOptions}
        branchCatalogs={branchCatalogs}
        initialCourseId={courseFilter}
        initialBranchId={branchFilter}
        onClose={() => setListGeneratorOpen(false)}
      />

      <PaymentSheet
        open={Boolean(paymentStudent)}
        onClose={() => {
          setPaymentStudent(null);
          setPaymentError('');
        }}
        student={paymentStudent ? toPaymentStudent(paymentStudent) : null}
        activeCourses={paymentActiveCourses}
        openRequests={openRequestsForPaymentStudent}
        paymentHistory={paymentHistoryForPaymentStudent}
        onSubmit={handlePaymentSubmit}
        saving={savingPayment}
        error={paymentError}
      />

      <StudentDetailsModal
        student={detailStudent}
        activeCourses={detailActiveCourses}
        openRequests={detailOpenRequests}
        paymentSchedule={detailPaymentSchedule}
        onClose={() => setDetailStudentId('')}
        onCreateAccount={handleCreateAccount}
        onResetPassword={handleCreateAccount}
        onDeleteAccount={handleDeleteAccount}
        onDeleteStudent={(student) => handleDelete(student.id)}
        onEdit={(student) => navigate('/add-student', { state: { existingStudent: student } })}
        onMarkPayment={(student) => setPaymentStudent(student)}
      />

      <CredentialsCard
        open={Boolean(credentialsResult)}
        onClose={() => setCredentialsResult(null)}
        result={credentialsResult}
      />
    </div>
  );
};

const StudentDetailsModal = ({
  student,
  activeCourses,
  openRequests,
  paymentSchedule,
  onClose,
  onCreateAccount,
  onResetPassword,
  onDeleteAccount,
  onDeleteStudent,
  onEdit,
  onMarkPayment,
}) => {
  if (!student) return null;

  const email = student.personalDetails?.email || student.email || '';
  const phone = student.personalDetails?.phone || student.phone || '';
  const hasLogin = hasStudentLogin(student);
  const canManageLogin = canManageStudentLogin(student);
  const platformPassword = getPlatformAssignedPassword(student, activeCourses);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
              Student details
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-stone-950">
              {getDisplayName(student)}
            </h2>
            <p className="mt-1 text-sm text-stone-600">
              {student.academyStudentId || student.studentNumber || 'No student ID'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-4">
            <div className="rounded-xl border border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Profile</p>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Email</dt>
                  <dd className="text-right text-stone-900">{email || '-'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Phone</dt>
                  <dd className="text-right text-stone-900">{phone || '-'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Status</dt>
                  <dd className="text-right text-stone-900">{student.status || 'active'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Login account</dt>
                  <dd className="text-right text-stone-900">
                    {hasLogin ? 'Created' : 'Not created'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-stone-500">Assigned password</dt>
                  <dd className="text-right font-semibold text-stone-900">
                    {platformPassword || '-'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-xl border border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Actions</p>
              <div className="mt-3 grid gap-2">
                {!hasLogin ? (
                  <button
                    type="button"
                    onClick={() => onCreateAccount(student)}
                    className="min-h-[42px] rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white"
                  >
                    Create account
                  </button>
                ) : canManageLogin ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onResetPassword(student)}
                      className="min-h-[42px] rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white"
                    >
                      Reset password
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteAccount(student)}
                      className="min-h-[42px] rounded-xl border border-red-200 px-4 text-sm font-semibold text-red-700"
                    >
                      Delete login account
                    </button>
                  </>
                ) : (
                  <p className="rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
                    Login already exists for this email.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => onMarkPayment(student)}
                  className="min-h-[42px] rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white"
                >
                  Mark payment
                </button>
                <button
                  type="button"
                  onClick={() => onEdit(student)}
                  className="min-h-[42px] rounded-xl border border-stone-300 px-4 text-sm font-semibold text-stone-700"
                >
                  Edit details
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteStudent(student)}
                  className="min-h-[42px] rounded-xl border border-red-300 px-4 text-sm font-semibold text-red-700"
                >
                  Delete student
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Enrolled courses</p>
              <div className="mt-3 space-y-2">
                {activeCourses.length ? (
                  activeCourses.map((course) => (
                    <div
                      key={`${course.courseId}-${course.batchId}`}
                      className="rounded-xl bg-stone-50 px-4 py-3"
                    >
                      <p className="text-sm font-semibold text-stone-950">
                        {course.courseName}
                      </p>
                      <p className="text-xs text-stone-600">
                        {course.batchName} - {course.branch}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-stone-500">No active course enrollments.</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Payment dues</p>
              <div className="mt-3 space-y-2">
                {openRequests.length ? (
                  openRequests.map((request) => (
                    <div key={request.id} className="rounded-xl bg-amber-50 px-4 py-3">
                      <p className="text-sm font-semibold text-stone-950">
                        {request.courseName || 'Course'}
                      </p>
                      <p className="text-xs text-stone-600">
                        {request.paymentType || 'Payment'} due {request.dueDate || '-'}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-stone-900">
                        LKR {Number(request.amount || 0).toLocaleString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-stone-500">No pending dues.</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Payment schedule</p>
              <div className="mt-3 space-y-2">
                {paymentSchedule?.length ? (
                  paymentSchedule.map((item) => {
                    const isUpcoming = item.kind === 'upcoming';
                    return (
                      <div
                        key={item.id}
                        className={`rounded-xl px-4 py-3 ${
                          isUpcoming ? 'bg-amber-50' : 'bg-emerald-50'
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-stone-950">
                              {item.courseName}
                            </p>
                            <p className="text-xs text-stone-600">
                              {item.label || 'Payment'} {item.date ? `- ${item.date}` : ''}
                            </p>
                          </div>
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              isUpcoming
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isUpcoming ? 'Upcoming' : 'Paid'}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="font-semibold text-stone-900">
                            LKR {Number(item.amount || 0).toLocaleString()}
                          </span>
                          {item.method ? (
                            <span className="text-xs text-stone-500">{item.method}</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-stone-500">
                    No payment records or upcoming dues yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentListGeneratorModal = ({
  open,
  rows,
  courses,
  branchOptions = [],
  branchCatalogs = {},
  initialCourseId = '',
  initialBranchId = '',
  onClose,
}) => {
  const [courseId, setCourseId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [includeEmail, setIncludeEmail] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectedCourseLabel =
    courses.find((course) => course.id === courseId)?.name ||
    courses.find((course) => course.id === courseId)?.title ||
    courseId ||
    'All courses';
  const selectedBranchLabel =
    branchOptions.find((branch) => branch.id === branchId)?.name ||
    branchId ||
    'All branches';

  useEffect(() => {
    if (!open) return;
    setCourseId(initialCourseId || '');
    setBranchId(initialBranchId || '');
    setIncludeEmail(false);
    setCopied(false);
  }, [open, initialCourseId, initialBranchId]);

  const generatedRows = useMemo(
    () =>
      rows
        .filter(({ student, activeCourses }) => {
          if (courseId && !activeCourses.some((course) => course.courseId === courseId)) {
            return false;
          }
          if (branchId) {
            const resolvedBranch = getStudentBranchRef(student, activeCourses, branchCatalogs);
            const matches =
              resolvedBranch?.id === branchId ||
              resolvedBranch?.slug === branchId ||
              resolvedBranch?.name === branchId;
            if (!matches) return false;
          }
          return true;
        })
        .map(({ student, activeCourses }) => {
          const selectedCourse = courseId
            ? activeCourses.find((course) => course.courseId === courseId)
            : activeCourses[0];
          return {
            name: getDisplayName(student),
            ...getPlatformPasswordParts(student, selectedCourse ? [selectedCourse] : activeCourses),
            email: student.personalDetails?.email || student.email || '',
            course: selectedCourse?.courseName || '',
            branch: getStudentBranchLabel(student, activeCourses, branchCatalogs),
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rows, courseId, branchId, branchCatalogs],
  );

  const listText = useMemo(() => {
    const meta = [
      `Course: ${selectedCourseLabel}`,
      `Branch: ${selectedBranchLabel}`,
      `Students: ${generatedRows.length}`,
      '',
    ];
    const header = includeEmail ? 'Name\tID\tEmail' : 'Name\tID';
    const body = generatedRows.map((row) =>
      includeEmail
        ? `${row.name}\t${row.studentNumber}\t${row.email}`
        : `${row.name}\t${row.studentNumber}`,
    );
    return [...meta, header, ...body].join('\n');
  }, [generatedRows, includeEmail, selectedCourseLabel, selectedBranchLabel]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(listText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const handleDownload = () => {
    const blob = new Blob([listText], { type: 'text/tab-separated-values;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student-list.tsv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return;
    const rowsHtml = generatedRows
      .map(
        (row) => `
          <tr>
            <td>${escapeHtml(row.name)}</td>
            <td>${escapeHtml(row.studentNumber)}</td>
            ${includeEmail ? `<td>${escapeHtml(row.email)}</td>` : ''}
          </tr>
        `,
      )
      .join('');
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Student password ID list</title>
          <style>
            body { font-family: Arial, sans-serif; color: #1c1917; padding: 24px; }
            h1 { font-size: 20px; margin: 0 0 8px; }
            p { margin: 3px 0; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 12px; }
            th, td { border: 1px solid #d6d3d1; padding: 7px; text-align: left; vertical-align: top; }
            th { background: #f5f5f4; }
          </style>
        </head>
        <body>
          <h1>Student password ID list</h1>
          <p><strong>Course:</strong> ${escapeHtml(selectedCourseLabel)}</p>
          <p><strong>Branch:</strong> ${escapeHtml(selectedBranchLabel)}</p>
          <p><strong>Students:</strong> ${generatedRows.length}</p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                ${includeEmail ? '<th>Email</th>' : ''}
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
              Student list
            </p>
            <h2 className="mt-1 text-2xl font-semibold text-stone-950">
              Generate student ID list
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-stone-500">
              Course
            </span>
            <select
              value={courseId}
              onChange={(event) => setCourseId(event.target.value)}
              className="block min-h-[44px] w-full rounded-xl border border-stone-300 px-3 text-sm"
            >
              <option value="">All courses</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name || course.title || course.id}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-stone-500">
              Branch
            </span>
            <select
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              className="block min-h-[44px] w-full rounded-xl border border-stone-300 px-3 text-sm"
            >
              <option value="">All branches</option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm font-medium text-stone-700">
          <input
            type="checkbox"
            checked={includeEmail}
            onChange={(event) => setIncludeEmail(event.target.checked)}
            className="h-4 w-4 rounded border-stone-300"
          />
          Include email addresses
        </label>

        <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-stone-900">
              {generatedRows.length} students
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopy}
                disabled={!generatedRows.length}
                className="min-h-[38px] rounded-xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 disabled:opacity-50"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={!generatedRows.length}
                className="min-h-[38px] rounded-xl bg-stone-900 px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Download
              </button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={!generatedRows.length}
                className="min-h-[38px] rounded-xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 disabled:opacity-50"
              >
                Print
              </button>
            </div>
          </div>
          <textarea
            readOnly
            value={listText}
            className="h-72 w-full resize-none rounded-xl border border-stone-300 bg-white p-3 font-mono text-xs text-stone-800"
          />
        </div>
      </div>
    </div>
  );
};

const BulkImportModal = ({
  open,
  courses,
  branchOptions = [],
  branchCatalogs = {},
  saving,
  onClose,
  onSubmit,
}) => {
  const [text, setText] = useState('');
  const [courseId, setCourseId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [branchChoice, setBranchChoice] = useState('__auto__');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setText('');
    setCourseId('');
    setBatchId('');
    setBranchChoice('__auto__');
    setError('');
  }, [open]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === courseId) || null,
    [courses, courseId],
  );
  const batches = useMemo(
    () => selectedCourse?.batches || [],
    [selectedCourse],
  );
  const selectedBatch = useMemo(
    () =>
      batches.find(
        (batch, index) =>
          String(batch.batchId || index) === String(batchId),
      ) || null,
    [batches, batchId],
  );
  const autoBranch = resolveBranchRef(selectedBatch || selectedCourse?.branch, branchCatalogs);

  useEffect(() => {
    if (!batches.length) {
      setBatchId('');
      return;
    }
    setBatchId(batches[0].batchId || '0');
  }, [batches]);

  if (!open) return null;

  const rows = parseBulkRows(text);

  const handleSubmit = async () => {
    try {
      setError('');
      const selectedBranch =
        branchChoice === '__auto__'
          ? autoBranch
          : branchChoice === '__none__'
            ? ''
            : branchOptions.find((branch) => branch.id === branchChoice) || null;
      await onSubmit({ text, courseId, batchId, branch: selectedBranch || null });
    } catch (err) {
      setError(err.message || 'Could not add students.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-stone-950">Bulk add students</h2>
            <p className="mt-1 text-sm text-stone-600">
              Paste one student per line: name, email, contact number.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Course
            </span>
            <select
              value={courseId}
              onChange={(event) => setCourseId(event.target.value)}
              className="block min-h-[48px] w-full rounded-xl border border-stone-300 px-3 text-sm"
            >
              <option value="">Select course</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name || course.title || course.id}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Batch
            </span>
            <select
              value={batchId}
              onChange={(event) => setBatchId(event.target.value)}
              disabled={!batches.length}
              className="block min-h-[48px] w-full rounded-xl border border-stone-300 px-3 text-sm disabled:bg-stone-100"
            >
              {batches.length ? (
                batches.map((batch, index) => (
                  <option key={batch.batchId || index} value={batch.batchId || String(index)}>
                    {batch.batchId || `Batch ${index + 1}`}
                  </option>
                ))
              ) : (
                <option value="">No batches</option>
              )}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Branch
            </span>
            <select
              value={branchChoice}
              onChange={(event) => setBranchChoice(event.target.value)}
              className="block min-h-[48px] w-full rounded-xl border border-stone-300 px-3 text-sm"
            >
              <option value="__auto__">
                {autoBranch?.name ? `Auto: ${autoBranch.name}` : 'Auto from batch/course'}
              </option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
              <option value="__none__">No branch</option>
            </select>
          </label>
        </div>

        <label className="mt-4 block">
          <span className="mb-1.5 block text-sm font-medium text-stone-700">
            Student list
          </span>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={8}
            placeholder={`A. Hashani Miyurangi Thilakarathne, hashani@example.com, +94771234567\nNimal Perera, nimal@example.com, 771234568`}
            className="block w-full rounded-xl border border-stone-300 px-3 py-2 font-mono text-sm"
          />
        </label>

        <div className="mt-3 text-sm text-stone-600">
          {rows.length} valid student{rows.length === 1 ? '' : 's'} detected.
        </div>

        {error ? (
          <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-xl border border-stone-300 px-4 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !rows.length || !courseId}
            className="min-h-[44px] rounded-xl bg-[#d61f74] px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Adding...' : 'Add students'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SPageStat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
    <p className="mt-0.5 truncate text-base font-semibold text-stone-950">{value}</p>
  </div>
);

const SPageField = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">{label}</span>
    {children}
  </label>
);

export default StudentsPage;
